import React from "react";
import PropTypes from "prop-types";

/**
 * Reusable Dropdown component
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the dropdown
 * @param {string} props.name - Name attribute for the dropdown
 * @param {string} props.value - Currently selected value
 * @param {function} props.onChange - Function to call when selection changes
 * @param {Array} props.options - Array of option objects with value and label properties
 * @param {string} props.label - Label text for the dropdown (optional)
 * @param {string} props.placeholder - Placeholder text (optional)
 * @param {string} props.className - Additional CSS classes (optional)
 * @param {boolean} props.disabled - Whether the dropdown is disabled (optional)
 */
const Dropdown = ({
                      id,
                      name,
                      value,
                      onChange,
                      options,
                      label,
                      placeholder,
                      className = "",
                      disabled = false
                  }) => {
    // Handle change internally and pass just the value to the callback
    const handleChange = (e) => {
        onChange(e.target.value);
    };

    return (
        <div className="dropdown-container">
            {label && (
                <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}
            <select
                id={id}
                name={name}
                value={value}
                onChange={handleChange}
                disabled={disabled}
                className={`p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                    disabled ? "bg-gray-100 text-gray-500" : ""
                } ${className}`}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

Dropdown.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired, // Now expects a function that receives the value directly
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired
        })
    ).isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool
};

export default Dropdown;